// src/auth/useAuth.ts — SCE Issues
// Hook centralisant les opérations d'authentification

import { useCallback, useEffect, useState } from 'react'
import { useIsAuthenticated, useMsal } from '@azure/msal-react'
import type { AccountInfo } from '@azure/msal-browser'
import { tokenRequest } from './msalConfig'
import { connecter, deconnecter, obtenirJeton } from './session'

export interface UseAuthReturn {
  isAuthenticated: boolean
  account: AccountInfo | null
  isReady: boolean
  login: () => Promise<void>
  logout: () => Promise<void>
  getToken: () => Promise<string | null>
  error: string | null
}

// ─── Bypass dev (VITE_DEV_BYPASS_AUTH=true) ───

const DEV_BYPASS = import.meta.env.VITE_DEV_BYPASS_AUTH === 'true'

const MOCK_ACCOUNT = {
  username: 'dev.user@sartorius.com',
  name: 'Dev User (bypass)',
  homeAccountId: 'dev-bypass',
  environment: 'login.microsoftonline.com',
  tenantId: 'dev-bypass',
  localAccountId: 'dev-bypass',
  idTokenClaims: {},
} as unknown as AccountInfo

function useAuthMock(): UseAuthReturn {
  return {
    isAuthenticated: true,
    account: MOCK_ACCOUNT,
    isReady: true,
    login: async () => {},
    logout: async () => {},
    getToken: async () => null,
    error: null,
  }
}

/**
 * Hook principal d'authentification.
 * Expose l'état + les actions login/logout/getToken.
 */
function useAuthReal(): UseAuthReturn {
  const { instance, accounts, inProgress } = useMsal()
  const isAuthenticated = useIsAuthenticated()
  const [error, setError] = useState<string | null>(null)

  // Compte actif (le premier par défaut)
  const account: AccountInfo | null = accounts.length > 0 ? (accounts[0] ?? null) : null

  // Prêt = MSAL n'est pas en cours d'une interaction
  const isReady = inProgress === 'none'

  // Assigne le compte actif à MSAL si absent
  useEffect(() => {
    if (account && !instance.getActiveAccount()) {
      instance.setActiveAccount(account)
    }
  }, [account, instance])

  /**
   * Connexion par redirection (Lot 10) : les popups sont bloquées dans le
   * navigateur intégré d'Outlook et de Teams. La page part vers Microsoft.
   */
  const login = useCallback(async (): Promise<void> => {
    setError(null)
    try {
      await connecter()
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erreur de connexion'
      setError(message)
      // eslint-disable-next-line no-console
      console.error('[useAuth] login failed:', err)
    }
  }, [])

  /**
   * Déconnexion par redirection, retenue : la reconnexion silencieuse ne
   * rouvrira pas la session au prochain passage (cf. auth/session.ts).
   */
  const logout = useCallback(async (): Promise<void> => {
    setError(null)
    try {
      await deconnecter()
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erreur de déconnexion'
      setError(message)
      // eslint-disable-next-line no-console
      console.error('[useAuth] logout failed:', err)
    }
  }, [])

  /**
   * Récupère un access token Graph API (silencieux, sinon interaction).
   */
  const getToken = useCallback(async (): Promise<string | null> => {
    if (!account) {
      return null
    }
    try {
      return await obtenirJeton(tokenRequest, account)
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[useAuth] token failed:', err)
      return null
    }
  }, [account])

  return {
    isAuthenticated,
    account,
    isReady,
    login,
    logout,
    getToken,
    error,
  }
}

export const useAuth = DEV_BYPASS ? useAuthMock : useAuthReal
